import { useCallback, useEffect, useRef, useState } from "react";

import { CloseIcon } from "@/components/icons/CloseIcon";
import { Logo } from "@/components/layout/Logo";
import { navLinks } from "@/content/site";
import type { NavLink } from "@/content/types";
import { useActiveSection } from "@/hooks/useActiveSection";
import { useHasScrolled } from "@/hooks/useHasScrolled";
import { cn } from "@/lib/cn";

const MENU_ID = "mobile-nav";

/** Section ids the scroll-spy watches, derived from the in-page nav links. */
const SECTION_IDS = navLinks.filter((link) => link.href.startsWith("#")).map((link) => link.href.slice(1));

type NavItemProps = {
  readonly link: NavLink;
  readonly variant: "desktop" | "drawer";
  readonly isActive: boolean;
  readonly onNavigate?: () => void;
};

function NavItem({ link, variant, isActive, onNavigate }: NavItemProps) {
  const isDesktop = variant === "desktop";

  if (isDesktop) {
    return (
      <a
        className={cn(
          "shrink-0 whitespace-nowrap font-medium tracking-wide transition-colors duration-200 lg:text-base xl:text-lg",
          "focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white",
          // The pill reads as a button, so it changes colour instead of underlining.
          link.highlighted
            ? "rounded-3xl bg-brand-blue px-5 py-2.5 hover:bg-brand-sky xl:px-7 xl:py-3"
            : cn("underline-offset-8 decoration-brand-sky decoration-[3px] hover:underline", isActive && "underline"),
        )}
        href={link.href}
        {...(link.external ? { target: "_blank", rel: "noreferrer" } : {})}
        {...(isActive ? { "aria-current": "location" as const } : {})}
      >
        {link.name}
      </a>
    );
  }

  return (
    <a
      className={cn(
        "flex items-center justify-between gap-4 py-4 text-lg font-bold tracking-wide transition-colors duration-200",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white",
        link.highlighted ? "text-brand-deep" : cn("text-white hover:text-brand-deep", isActive && "text-brand-deep"),
      )}
      href={link.href}
      onClick={onNavigate}
      {...(link.external ? { target: "_blank", rel: "noreferrer" } : {})}
      {...(isActive ? { "aria-current": "location" as const } : {})}
    >
      <span>{link.name}</span>

      {/* A dot marks the section you're in; the pill link gets an arrow since
          it leaves the page. */}
      {link.external ? (
        <span aria-hidden className="text-brand-deep/70">
          ↗
        </span>
      ) : (
        <span aria-hidden className={cn("h-2 w-2 shrink-0 rounded-full transition-colors", isActive ? "bg-brand-deep" : "bg-transparent")} />
      )}
    </a>
  );
}

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hasScrolled = useHasScrolled();
  const activeSection = useActiveSection(SECTION_IDS);

  const toggleRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isMenuOpen, closeMenu]);

  // Keep the page behind the drawer from scrolling while it's open.
  useEffect(() => {
    if (!isMenuOpen) return;

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMenuOpen]);

  // Move focus into the drawer on open, and back to the toggle on close.
  useEffect(() => {
    if (isMenuOpen) {
      closeRef.current?.focus();
    } else if (document.activeElement === closeRef.current) {
      toggleRef.current?.focus();
    }
  }, [isMenuOpen]);

  const isLinkActive = (link: NavLink) => link.href.startsWith("#") && link.href.slice(1) === activeSection;

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 text-white transition-[background-color,box-shadow,backdrop-filter] duration-300",
        hasScrolled ? "bg-brand-navy/85 backdrop-blur-md shadow-lg shadow-black/30" : "bg-brand-navy",
      )}
    >
      <div className="container mx-auto flex h-20 lg:h-28 items-center justify-between px-5">
        <a href="#top" className="shrink-0 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
          <Logo className="h-8 lg:h-10 w-auto text-white" />
        </a>

        <nav aria-label="Principal" className="hidden items-center justify-center gap-6 uppercase lg:flex xl:gap-9">
          {navLinks.map((link) => (
            <NavItem key={link.name} link={link} variant="desktop" isActive={isLinkActive(link)} />
          ))}
        </nav>

        <button
          ref={toggleRef}
          type="button"
          onClick={() => {
            setIsMenuOpen((open) => !open);
          }}
          aria-expanded={isMenuOpen}
          aria-controls={MENU_ID}
          aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
          className="-mr-2 rounded p-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white lg:hidden"
        >
          {/* The bars fold into a cross while the drawer opens. */}
          <span className="flex h-6 w-8 flex-col justify-center gap-2">
            <span className={cn("block h-0.5 w-8 bg-white transition-transform duration-300", isMenuOpen && "translate-y-2.5 rotate-45")} />
            <span className={cn("block h-0.5 w-8 bg-white transition-opacity duration-200", isMenuOpen && "opacity-0")} />
            <span className={cn("block h-0.5 w-8 bg-white transition-transform duration-300", isMenuOpen && "-translate-y-2.5 -rotate-45")} />
          </span>
        </button>
      </div>

      <div
        aria-hidden
        onClick={closeMenu}
        className={cn(
          "fixed inset-0 bg-brand-navy/70 backdrop-blur-sm transition-opacity duration-300 lg:hidden",
          isMenuOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      {/* `invisible` when closed keeps the links out of the tab order, and it's
          part of the transition so it only applies once the slide finishes. */}
      <nav
        id={MENU_ID}
        aria-label="Menu"
        className={cn(
          "fixed right-0 top-0 flex h-dvh w-[86%] max-w-sm flex-col bg-brand-blue px-6 pb-8 pt-5 shadow-2xl shadow-black/40",
          "transition-[transform,visibility] duration-300 ease-out lg:hidden",
          isMenuOpen ? "translate-x-0" : "invisible translate-x-full",
        )}
      >
        <div className="flex items-center justify-between">
          <Logo className="h-7 w-auto text-white" />

          <button
            ref={closeRef}
            type="button"
            onClick={closeMenu}
            aria-label="Fechar menu"
            className="-mr-2 rounded p-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <CloseIcon className="h-6 w-6 fill-brand-deep transition-colors hover:fill-white" />
          </button>
        </div>

        <div className="mt-8 flex flex-col divide-y divide-white/20 uppercase">
          {navLinks.map((link) => (
            <NavItem key={link.name} link={link} variant="drawer" isActive={isLinkActive(link)} onNavigate={closeMenu} />
          ))}
        </div>
      </nav>
    </header>
  );
}
